import React, {useRef, useState, MouseEvent} from 'react';
import FormControl from '@material-ui/core/FormControl';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DirectionAuto from '../DirectionAuto';
import {useSelector} from 'react-redux';
import {selectRTL} from '../../../selectors';
import {useTrackEvent, UseTrackEventProps} from '../../../hooks';
import useStyles, {
    StyledTextField,
    StyledTextFieldProps,
    StyledPopover,
    StyledInputAdornment
} from './Select.styles';

export interface SelectOption {
    label: number | string;
    value: unknown;
}

export interface SelectProps extends StyledTextFieldProps {
    wrapperClassName?: string;
    options: SelectOption[];
    onChange?: (option: SelectOption) => void;
    onBlur?: (option: SelectOption) => void;
    value: unknown;
    trackEventProps?: UseTrackEventProps;
}

const Select = React.forwardRef((props: SelectProps, textFieldRef: React.Ref<HTMLDivElement>) => {
    const {
        wrapperClassName,
        options = [],
        onChange = () => {},
        onBlur = () => {},
        trackEventProps,
        ...textFieldProps
    } = props;

    const styles = useStyles();
    const isRTL = useSelector(selectRTL);
    const textFieldWrapperRef = useRef<HTMLDivElement>();
    const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
    const trackEvent = useTrackEvent(trackEventProps);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        const _anchorEl = anchorEl ? null : event.currentTarget;
        setAnchorEl(_anchorEl);
        if (!!_anchorEl === true) {
            trackEvent('Begin', {data: {opened: true}});
        }
    };

    const handleOptionChange = (option: SelectOption) => {
        setAnchorEl(null);
        onChange(option);
        onBlur(option);
        trackEvent('Success', {data: option});
    };

    const open = !!anchorEl;
    const dropDownWidth = textFieldWrapperRef.current && textFieldWrapperRef.current.offsetWidth;

    return (
        <FormControl className={wrapperClassName}>
            <div style={{position: 'relative', width: '100%'}} ref={textFieldWrapperRef}>
                <StyledTextField
                    focused={open}
                    variant="outlined"
                    ref={textFieldRef}
                    onClick={handleClick}
                    InputProps={{
                        readOnly: true,
                        [isRTL ? 'startAdornment' : 'endAdornment']: (
                            <StyledInputAdornment position="end">
                                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </StyledInputAdornment>
                        )
                    }}
                    {...textFieldProps}
                />
            </div>

            <StyledPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClick}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                PaperProps={{
                    style: {width: dropDownWidth}
                }}>
                <MenuList className={styles.menuList}>
                    {options.map((option, index) => (
                        <MenuItem
                            key={`item-${index}`}
                            className={styles.menuListItem}
                            tabIndex={index}
                            value={index}
                            onClick={() => {
                                handleOptionChange(option);
                            }}>
                            <DirectionAuto>{option.label}</DirectionAuto>
                        </MenuItem>
                    ))}
                </MenuList>
            </StyledPopover>
        </FormControl>
    );
});

export default Select;
